import { Checkbox, FormControl, FormControlLabel, Grid } from '@material-ui/core';
import { MaterialUiPickersDate } from '@material-ui/pickers/typings/date';
import { Button, Card, Icons, TextField, Typography } from '@nutrien/cxp-components';
import React, { useEffect, useMemo, useState } from 'react';
import { Subscription } from 'rxjs';

import { useMst } from '../../mobx-models/Root';
import { InspectionTaskListType } from '../../models/models';
import { PreviousTaskCommentsDocument } from '../../rxdb/previousTaskComments/queryBuilder';
import { TaskList, TaskListDocument } from '../../rxdb/taskList/queryBuilder';
import { ViewInspectorDocument } from '../../rxdb/viewInspector/queryBuilder';
import { useViewInspector } from '../../rxdb/viewInspector/useViewInspector';
import { InspectionStatus, InspectorRole, TaskStatus } from '../../utilities/enums';
import {
  getDateTimeFormat,
  getValueFromLocalStorage,
  isNull,
} from '../../utilities/utilityFunctions';
import AssignInspector from '../AssignInspector';
import Calender from '../Calender';
import StyledButton from '../InspectorInfo/StyledButton';
import { useStyles } from './index';

interface Props {
  validateFIStamping: () => boolean;
  taskData: TaskList | null;
  isElectrical: boolean;
  completeInspectionPayload: TaskListDocument;
  openConfirmation: (value: boolean) => void;
  needsAttention: boolean;
  isAttentionFlag: boolean;
  taskComments: string;
  submitInspection: () => void;
  onRecommendationsInput: (event: React.FocusEvent<HTMLInputElement>) => void;
  handleKeyDown: (event: React.KeyboardEvent<HTMLDivElement>) => void;
  handleChange: (event: MaterialUiPickersDate) => void;
  handleSave: () => void;
  onInspectorCommentChange: (event: React.FocusEvent<HTMLInputElement>) => void;
  showComment: boolean;
  handleCancelComment: () => void;
  checkValid: () => boolean;
  handleSaveComment: () => void;
  previousTaskComments: PreviousTaskCommentsDocument[];
  handleNeedAttentionChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  isLiFiSameAndAdhoc: boolean;
  invalidDate: boolean;
  maxDateError: boolean;
  handleRequired: (event: MaterialUiPickersDate) => void;
  requiredDate: boolean;
  isLiFiSame: boolean;
  comingfromSignAndStamp: boolean;
  selectedInspection: InspectionTaskListType;
  isEditable: boolean;
  selectedProxyInspector: { fieldInspectorName: string };
  handleProxyInspectorChange: (val: { fieldInspectorName: '' }) => void;
}

const CompleteInspectionAccordion: React.FC<Props> = ({
  validateFIStamping,
  isElectrical,
  taskData,
  completeInspectionPayload,
  openConfirmation,
  needsAttention,
  isAttentionFlag,
  taskComments,
  submitInspection,
  onRecommendationsInput,
  handleKeyDown,
  handleChange,
  handleSave,
  onInspectorCommentChange,
  showComment,
  handleCancelComment,
  checkValid,
  handleSaveComment,
  previousTaskComments,
  handleNeedAttentionChange,
  isLiFiSameAndAdhoc,
  invalidDate,
  maxDateError,
  handleRequired,
  requiredDate,
  isLiFiSame,
  selectedInspection,
  comingfromSignAndStamp,
  isEditable,
  selectedProxyInspector,
  handleProxyInspectorChange,
}) => {
  const classes = useStyles();
  const { saveInspection, CompletionDate, CurrentUserSelected } = useMst();
  const [isTaskFISignPending, setIsTaskFISignPending] = useState(false);
  const [isTaskLISignPending, setIsTaskLISignPending] = useState(false);
  const [openAssignInspector, setOpenAssignInspector] = useState<boolean>(false);
  const { getInspectorOnWorkPackage, viewInspectorInitialized, viewInspectorCollection } =
    useViewInspector();
  const [inspectorData, setInspectorData] = useState<ViewInspectorDocument[]>([]);
  const workPackageId = CurrentUserSelected.workPackageId;
  const savedDate = CompletionDate.getCompletedDate;
  const currentUser = CurrentUserSelected.currentUser;
  const inspectionStatus = selectedInspection?.assetInspectionStatusId;

  /* if taskStatus is LiSignPending and InspectionStatus is not LiSignPending 
    then we should disable sign and stamp button 
    But should ignore 
    when user coming back from sign and stamp */
  const isInspectionStatusLiSign =
    taskData?.fkInspectionTaskStatusCategory === TaskStatus.LI_SIGN_PENDING &&
    Number(inspectionStatus) !== InspectionStatus.LI_SIGN_PENDING &&
    !CurrentUserSelected.isAdhoc &&
    !comingfromSignAndStamp;

  const validateLeadForAddingComment = () => {
    const taskStatus = CurrentUserSelected.isAdhoc
      ? [TaskStatus.LI_PENDING_APPROVAL]
      : [TaskStatus.LI_PENDING_APPROVAL, TaskStatus.LI_SIGN_PENDING];
    const inspectionLevelStatus = CurrentUserSelected.isAdhoc
      ? [InspectionStatus.LI_PENDING_APPROVAL]
      : [InspectionStatus.LI_PENDING_APPROVAL, InspectionStatus.LI_SIGN_PENDING];
    return (
      !isLiFiSame &&
      taskData?.fkInspectorLead === currentUser?.inspectorId &&
      taskStatus.includes(taskData?.fkInspectionTaskStatusCategory) &&
      inspectionLevelStatus.includes(Number(inspectionStatus))
    );
  };

  const validateFieldForAddingComment = () => {
    return (
      isEditable &&
      taskData?.fkInspectionTaskStatusCategory === TaskStatus.IN_PROGRESS &&
      taskData?.isAssignedBack
    );
  };

  const validateComments = () => {
    return (
      previousTaskComments?.length !== 0 ||
      validateLeadForAddingComment() ||
      validateFieldForAddingComment()
    );
  };

  const handleClick = () => {
    if (isEditable) {
      setOpenAssignInspector(true);
    }
  };

  const getViewInspectorDataFromRxdb = async () => {
    const viewInspectorDocumentData = await getInspectorOnWorkPackage(workPackageId);
    if (viewInspectorDocumentData) {
      setInspectorData(viewInspectorDocumentData);
    }
  };

  const disableDECSubmit = useMemo(() => {
    return !saveInspection.isSaved && saveInspection.isChanged;
  }, [saveInspection.isSaved, saveInspection.isChanged]);

  useEffect(() => {
    let viewInspectorSub: Subscription;
    if (viewInspectorInitialized) {
      getViewInspectorDataFromRxdb();
      if (viewInspectorCollection) {
        const queryObj = {
          selector: {
            workPackageId: { $eq: workPackageId },
          },
        };
        viewInspectorSub = viewInspectorCollection.find(queryObj).$.subscribe(() => {
          getViewInspectorDataFromRxdb();
        });
      }
    }
    return () => {
      if (viewInspectorSub && viewInspectorSub.unsubscribe) viewInspectorSub.unsubscribe();
    };
  }, [viewInspectorInitialized]);

  useEffect(() => {
    setIsTaskFISignPending(
      taskData?.fkInspectionTaskStatusCategory === TaskStatus.FI_SIGN_PENDING &&
        !CurrentUserSelected.isAdhoc,
    );
    setIsTaskLISignPending(
      taskData?.fkInspectionTaskStatusCategory === TaskStatus.LI_SIGN_PENDING &&
        !CurrentUserSelected.isAdhoc &&
        inspectionStatus === InspectionStatus.LI_SIGN_PENDING,
    );
  }, [taskData]);

  return (
    <Grid container xs={12} direction="column" className={classes.ciAccordianGrid}>
      <Card
        elevation={1}
        className={
          validateFIStamping() === false ||
          isLiFiSameAndAdhoc ||
          isTaskFISignPending ||
          taskData?.fkInspectionTaskStatusCategory === TaskStatus.LI_SIGN_PENDING
            ? 'cardView'
            : 'card1'
        }
      >
        <Grid container xs={12} className={classes.submitInspectionGrid}>
          <Grid item xs={7}>
            {validateFIStamping() === false ||
            isLiFiSameAndAdhoc ||
            isTaskFISignPending ||
            taskData?.fkInspectionTaskStatusCategory === TaskStatus.LI_SIGN_PENDING ? (
              <>
                <Typography className="submitInspectionTitle">
                  {isElectrical ? 'Complete Preventative Maintenance' : 'Complete Inspection'}
                </Typography>
                {isElectrical && (
                  <Grid className={classes.subtitleGrid}>
                    <Typography className="subtitle">
                      * Mandatory to complete the PM Checklist before submitting the Preventative
                      Maintenance
                    </Typography>
                  </Grid>
                )}
              </>
            ) : null}
          </Grid>
          <Grid
            item
            xs={5}
            container
            justifyContent="flex-end"
            alignItems="center"
            className={
              completeInspectionPayload?.fkInspectionTaskStatusCategory === TaskStatus.COMPLETED
                ? classes.hideCompleteInspection
                : classes.showCompleteInspection
            }
          >
            {validateLeadForAddingComment() &&
            [
              TaskStatus.LI_PENDING_APPROVAL,
              TaskStatus.SC_PENDING_APPROVAL,
              TaskStatus.LI_SIGN_PENDING,
            ].includes(taskData?.fkInspectionTaskStatusCategory) ? (
              <>
                {!isLiFiSame && (
                  <Grid item className={classes.assignBackButtonGrid}>
                    <Button
                      noMinHeight
                      color="primary"
                      variant="outlined"
                      onClick={() => openConfirmation(false)}
                      className="assignBackButton"
                      disabled={
                        !taskData?.clerkId
                          ? (needsAttention && !taskComments?.length) || showComment
                          : disableDECSubmit
                      }
                      data-testid="assignBack-id"
                    >
                      <Typography>Assign Back</Typography>
                    </Button>
                  </Grid>
                )}
                <Grid item className={classes.submitReviewButtonGrid}>
                  <Button
                    disabled={
                      !taskData?.clerkId
                        ? needsAttention ||
                          isAttentionFlag ||
                          showComment ||
                          isInspectionStatusLiSign
                        : disableDECSubmit
                    }
                    noMinHeight
                    color="primary"
                    variant="contained"
                    onClick={() => openConfirmation(true)}
                    id={
                      needsAttention || isAttentionFlag || showComment
                        ? 'submitReviewButtonDisabled'
                        : 'submitReviewButton'
                    }
                    data-testid="submitForreview-id"
                  >
                    <Icons.CheckFeather />
                    <Grid className={classes.submitInspectionTextGrid}>
                      <Typography className="submitInspectionText">
                        {isTaskLISignPending || comingfromSignAndStamp
                          ? 'Signature & Stamping'
                          : 'Submit for Review'}
                      </Typography>
                    </Grid>
                  </Button>
                </Grid>
              </>
            ) : (
              <>
                {isEditable && (
                  <Grid item className={classes.submitInsButtonGrid}>
                    <Button
                      data-testid="submitButton"
                      disabled={
                        !saveInspection.isSaved ||
                        saveInspection.isChanged ||
                        !taskData?.pkAssetInspectionTask
                      }
                      noMinHeight
                      color="primary"
                      variant="contained"
                      onClick={submitInspection}
                      className={
                        !saveInspection.isSaved ||
                        saveInspection.isChanged ||
                        !taskData?.pkAssetInspectionTask
                          ? 'submitInsButtonDisabled'
                          : 'submitInsButtonEnabled'
                      }
                    >
                      {!isTaskFISignPending && <Icons.CheckFeather />}
                      <Grid className={classes.submitInspectionTextGrid}>
                        <Typography className="submitInspectionText">
                          {isTaskFISignPending ||
                          comingfromSignAndStamp ||
                          (isLiFiSame && isTaskLISignPending)
                            ? 'Signature & Stamping'
                            : `Submit ${isElectrical ? 'PM' : 'Inspection'}`}
                        </Typography>
                      </Grid>
                    </Button>
                  </Grid>
                )}
              </>
            )}
          </Grid>
        </Grid>
        <Grid className={classes.completeInsInnerGrid}>
          <Card elevation={1} className="completeInsInnerCard">
            <Grid className={classes.textFieldGrid} container xs={12}>
              <Typography
                variant="subtitle2"
                color="primary"
                className={
                  isEditable ? classes.recommendationsLabel : 'recommendationsLabelDisabled'
                }
              >
                {isElectrical ? 'Additional Comments' : 'Recommendations'}
              </Typography>
              <TextField
                disabled={!isEditable}
                value={isNull(completeInspectionPayload, 'recommendations', '')}
                onChange={onRecommendationsInput}
                onKeyDown={handleKeyDown}
                noMinHeight
                fullWidth
                variant="outlined"
                size="small"
                multiline
                rows={4}
                rowsMax={4}
                className={isEditable ? 'recommendationTextEnabled' : 'recommendationTextDisabled'}
                data-testid="comments-id"
              />
            </Grid>

            <Grid container xs={12}>
              <Grid
                container
                xs={10}
                className={classes.completionDateGrid}
                justifyContent="flex-start"
                alignItems="flex-start"
              >
                <Grid item xs={10}>
                  <Typography
                    variant="subtitle2"
                    color="primary"
                    className={isEditable ? 'completioDateLabel' : 'completioDateLabelDisabled'}
                  >
                    {isElectrical ? 'PM Completion Date' : 'Inspection completion date'}
                  </Typography>
                  <FormControl
                    fullWidth
                    variant="outlined"
                    data-testid="icd-id"
                    className="dateLabel"
                    style={{ display: '-webkit-inline-box' }}
                  >
                    <Calender
                      name="completedDate"
                      labelClass="form-label col-form-label col-lg-2 col-sm-3"
                      inputClass="col-lg-5 col-sm-5"
                      onChange={e => (e ? handleChange(e) : handleRequired(e))}
                      onKeyDown={handleKeyDown}
                      InputProps={{
                        disabled: !isEditable,
                        title: '',
                        id: 'l0-install-date',
                        maxDate: new Date(),
                        errorInvalid: invalidDate,
                        maxDateError,
                        requiredDate,
                        value: isNull(
                          savedDate,
                          'date',
                          isNull(completeInspectionPayload, 'dateCompleted', null),
                        ),
                      }}
                      tooltipTitle={undefined}
                      className="calendarControl"
                    />
                    {taskData?.clerkId && (
                      <>
                        <Typography className={classes.inspectedBy} onClick={handleClick}>
                          <Typography
                            variant="subtitle2"
                            color="primary"
                            display="inline"
                            style={{ fontWeight: '700' }}
                            className={
                              isEditable ? 'completioDateLabel' : 'completioDateLabelDisabled'
                            }
                          >
                            {'Inspected by'}
                          </Typography>
                          <Typography display="inline">
                            <Icons.PlusCircleFeather
                              className={
                                isEditable ? 'completioDateLabel' : 'completioDateLabelDisabled'
                              }
                              style={{ margin: '3px 0 0 3px' }}
                              color={isEditable ? 'primary' : 'gray'}
                              strokeWidth="1.5px"
                            />
                          </Typography>
                        </Typography>

                        {selectedProxyInspector?.fieldInspectorName && (
                          <Typography style={{ marginLeft: '10px' }}>
                            <StyledButton
                              inspector={{
                                fieldInspectorName: selectedProxyInspector?.fieldInspectorName,
                              }}
                              handleDeleteInspector={() => {
                                handleProxyInspectorChange({
                                  fieldInspectorName: '',
                                });
                              }}
                              count={0}
                              canDelete={isEditable}
                            />
                          </Typography>
                        )}
                      </>
                    )}
                  </FormControl>
                </Grid>
              </Grid>

              {isEditable && (
                <Grid item xs={2} container justifyContent="flex-end" alignItems="flex-end">
                  <Grid className={classes.recommandationSaveButtonGrid} item>
                    <Button
                      id={
                        saveInspection.isSaved
                          ? 'recommandationSaveButton'
                          : 'recommandationSaveButtonDisabled'
                      }
                      noMinHeight
                      color="primary"
                      variant="outlined"
                      onClick={handleSave}
                      disabled={saveInspection.isSaved}
                      data-testid="saveButton-id"
                    >
                      <Typography fontWeight={700}>Save</Typography>
                    </Button>
                  </Grid>
                </Grid>
              )}
            </Grid>
          </Card>
        </Grid>
      </Card>
      {validateComments() && (
        <Card elevation={1} className="card2">
          <Grid container xs={12} className={classes.commentsGrid}>
            <Grid item xs={8}>
              <Typography className="commentsLabel" data-testid="commentsField-id">
                Comments
              </Typography>
            </Grid>
            <Grid item xs={4} container justifyContent="flex-end" alignItems="center">
              <Grid
                item
                className={showComment ? classes.cancelButtonGrid : classes.cancelButtonGridHide}
              >
                <Grid>
                  <Button
                    noMinHeight
                    hidden={!showComment}
                    color="primary"
                    variant="outlined"
                    onClick={handleCancelComment}
                    className={showComment ? 'cancelButton' : 'cancelButtonHide'}
                    data-testid="reviewcancel-id"
                  >
                    <Typography>Cancel</Typography>
                  </Button>
                </Grid>
              </Grid>
              <Grid item className={classes.saveCommentButtonGrid}>
                <Button
                  disabled={checkValid()}
                  noMinHeight
                  hidden={!showComment}
                  color="primary"
                  variant="contained"
                  className={`${showComment ? 'saveCommentButton' : 'saveCommentButtonHide'} `}
                  id={`${checkValid() ? 'saveCommentButtonBg' : 'saveCommentButtonNoBg'}`}
                  onClick={handleSaveComment}
                  data-testid="reviewsave-id"
                >
                  <Grid className={classes.submitInspectionTextGrid}>
                    <Typography className="submitInspectionText">Save</Typography>
                  </Grid>
                </Button>
              </Grid>
            </Grid>
          </Grid>
          <Grid className={classes.liCommentsGrid}>
            <Card elevation={1} className="liCommentsCard">
              {validateLeadForAddingComment() && (
                <>
                  <Grid container xs={12}>
                    <Typography
                      variant="subtitle2"
                      color="primary"
                      className={classes.leadInspectorCommentsLabel}
                      data-testid="leadinspector-id"
                    >
                      Lead Inspector Comments
                    </Typography>

                    <TextField
                      disabled={!validateLeadForAddingComment()}
                      noMinHeight
                      fullWidth
                      variant="outlined"
                      size="small"
                      multiline
                      rows={4}
                      className={classes.commentsTextField}
                      defaultValue={isNull(taskData, 'commentInspectorField', '')}
                      error={needsAttention && !taskComments?.length}
                      errorText="Comment is mandatory if need attention checked"
                      value={taskComments}
                      required={taskData?.attentionFlag}
                      debounceTime={7000}
                      onChange={onInspectorCommentChange}
                      data-testid="leadinspectorcomments-id"
                    />

                    <Grid container xs={12}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            color="primary"
                            checked={!!needsAttention}
                            onChange={handleNeedAttentionChange}
                            data-testid="checkbox-id"
                          />
                        }
                        label="Need Attention for this Inspection"
                      />
                      <Grid className={classes.checkBoxText}>
                        <Typography className="subtitle">
                          (check if revision required for the Inspector)
                        </Typography>
                      </Grid>
                    </Grid>
                  </Grid>
                </>
              )}
              {validateFieldForAddingComment() && (
                <>
                  <Grid container xs={12}>
                    <Typography
                      variant="subtitle2"
                      color="primary"
                      className={classes.fiCommentsLabel}
                    >
                      {!taskData?.clerkId ? 'Field Inspector' : 'Data Entry Clerk'} Comments
                    </Typography>

                    <TextField
                      disabled={!isEditable}
                      noMinHeight
                      fullWidth
                      variant="outlined"
                      size="small"
                      multiline
                      rows={4}
                      className={classes.fiCommentsTextField}
                      defaultValue={isNull(taskData, 'commentInspectorField', '')}
                      value={taskComments}
                      required={taskData?.attentionFlag}
                      debounceTime={7000}
                      onChange={onInspectorCommentChange}
                    />
                  </Grid>
                </>
              )}
              {previousTaskComments?.length > 0 && (
                <>
                  <Grid className={classes.previousCommentsLabelGrid}>
                    <Typography variant="subtitle2" className="previousCommentsLabel">
                      Previous Comments
                    </Typography>
                  </Grid>

                  {previousTaskComments?.map((e: PreviousTaskCommentsDocument) => (
                    <>
                      <Grid className={classes.dateTimeGrid}>
                        <Typography variant="subtitle2" className="dateTimeText">
                          {e.inspectorRoleId === InspectorRole.LEAD &&
                            `Lead Inspector Review Findings`}
                          {e.inspectorRoleId === InspectorRole.SC &&
                            `Site Coordinator Review Findings`}
                          {e.inspectorRoleId === InspectorRole.FIELD && `Field Inspector Comments`}
                          {e.inspectorRoleId === InspectorRole.DEC && `Data Entry Clerk Comments`}
                          {`${getDateTimeFormat(e.dateCreated)}`}
                        </Typography>
                      </Grid>
                      <TextField
                        disabled
                        noMinHeight
                        fullWidth
                        variant="outlined"
                        size="small"
                        minRows="2"
                        multiline
                        className={classes.preivousCommentBox}
                        defaultValue={e?.comment ? e?.comment : ''}
                        debounceTime={7000}
                        data-testid="commentbox-id"
                      />
                      {(e.inspectorRoleId === InspectorRole.LEAD ||
                        e.inspectorRoleId === InspectorRole.SC) &&
                        e.attentionFlag && (
                          <Grid container xs={12}>
                            <FormControlLabel
                              disabled
                              control={
                                <Checkbox disabled color="primary" checked={e.attentionFlag} />
                              }
                              label="Need Attention for this Inspection"
                            />
                          </Grid>
                        )}
                    </>
                  ))}
                </>
              )}
            </Card>
          </Grid>
        </Card>
      )}
      {openAssignInspector && (
        <AssignInspector
          title="Add Inspector"
          isUpdateInspector={false}
          openAssignInspector={openAssignInspector}
          setOpenAssignInspector={setOpenAssignInspector}
          fieldInspectorList={[...JSON.parse(JSON.stringify(inspectorData))]}
          leadInspectorId={Number(getValueFromLocalStorage('leadInspectorId'))}
          inspectionId={CurrentUserSelected.inspectionId}
          inspectionLevelComment={''}
          isAssignToLead={false}
          taskList={selectedInspection.taskList}
          selectedProxyInspector={selectedProxyInspector}
          isFromInspectedBy={true}
          handleProxyInspectorChange={handleProxyInspectorChange}
        />
      )}
    </Grid>
  );
};

export default CompleteInspectionAccordion;
